/**
 * Email — сбор email клиента (шаг Flow step="email"). Сохраняет адрес в user.email; писем не шлёт.
 * Пропсы: initial? (предзаполнение), onBack(), onNext(email).
 */
import { useCallback, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { Field } from "@/components/Field";
import { authApi, friendlyError } from "@/lib/api";

type Props = {
  initial?: string;
  onBack: () => void;
  onNext: (email: string) => void;
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function Email({ initial, onBack, onNext }: Props) {
  const [email, setEmail] = useState(initial ?? "");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const value = email.trim().toLowerCase();
  const valid = EMAIL_RE.test(value);
  const showErr = (touched && email && !valid ? "Проверьте адрес — формат name@example.com" : undefined) ?? err ?? undefined;

  const submit = useCallback(async () => {
    if (!valid || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await authApi.setEmail(value);
      onNext(r.email);
    } catch (e) {
      setErr(friendlyError(e, "Не удалось сохранить email. Попробуйте ещё раз."));
    } finally {
      setLoading(false);
    }
  }, [valid, loading, value, onNext]);

  useBackButton(onBack);
  useMainButton({
    text: loading ? "Сохраняем..." : "Продолжить",
    onClick: submit,
    active: valid,
    loading,
  });

  return (
    <Screen>
      <Hero
        icon="✉️"
        title="Ваш email"
        subtitle="Укажите действующий адрес — на него придёт код для подписания документов. Введите его без ошибок."
      />

      <Field
        type="email"
        inputMode="email"
        autoFocus
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder="name@example.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setTouched(true);
          setErr(null);
        }}
        onBlur={() => setTouched(true)}
        error={showErr}
      />

      <Note className="mt-5">
        На этот адрес будут приходить коды для подписания договора-оферты. Убедитесь, что у вас есть
        доступ к почте.
      </Note>

      <FallbackButton text="Продолжить" onClick={submit} disabled={!valid} loading={loading} />
    </Screen>
  );
}